/**
 * CommunicationsPage
 *
 * Customer communications history page.
 * Displays all communications grouped by type with filtering options.
 *
 * Requirements: 10.1, 10.2 - Display chronological list grouped by type
 */

import { useState, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import {
  Button,
  Tabs,
  TabsList,
  TabsTrigger,
  Switch,
  Label,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from '@bloomcode/ui';
import { RefreshCw, List as ListIcon, LayoutGrid } from '@bloomcode/icons';
import { useCommunications, useCommunicationMutations } from '@/hooks/useCommunications';
import { CommunicationList, CommunicationDetail } from '@/components/communications';
import type { CommunicationItem, CommunicationDetailData } from '@/components/communications';
import type { CommunicationType } from '@/components/dashboard';

type TypeFilter = 'all' | CommunicationType;

export default function CommunicationsPage() {
  const navigate = useNavigate();
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');
  const [groupByType, setGroupByType] = useState(true);
  const [selectedCommunication, setSelectedCommunication] = useState<CommunicationItem | null>(
    null
  );

  const { communications, isLoading, refetch } = useCommunications({
    type: typeFilter === 'all' ? undefined : typeFilter,
  });

  const { markAsRead } = useCommunicationMutations();

  const handleCommunicationClick = useCallback(
    async (communication: CommunicationItem) => {
      // Mark as read if unread
      if (!communication.isRead) {
        try {
          await markAsRead(communication.id);
        } catch {
          // Silently fail - not critical
        }
      }

      // Handle navigation based on type
      if (communication.type === 'ticket' && communication.link) {
        // Navigate to ticket detail page
        navigate(`/tickets/${communication.link}`);
      } else {
        // Show communication detail in modal/panel
        setSelectedCommunication(communication);
      }
    },
    [markAsRead, navigate]
  );

  const handleBackFromDetail = useCallback(() => {
    setSelectedCommunication(null);
  }, []);

  const handleNavigateToTicket = useCallback(
    (ticketId: string) => {
      navigate(`/tickets/${ticketId}`);
    },
    [navigate]
  );

  // Filter tabs configuration
  const filterTabs: { value: TypeFilter; label: string }[] = [
    { value: 'all', label: 'All' },
    { value: 'ticket', label: 'Tickets' },
    { value: 'invoice', label: 'Invoices' },
    { value: 'announcement', label: 'Announcements' },
    { value: 'email', label: 'Emails' },
    { value: 'message', label: 'Messages' },
  ];

  // If a communication is selected, show detail view
  if (selectedCommunication) {
    // Convert CommunicationItem to CommunicationDetailData
    const detailData: CommunicationDetailData = {
      ...selectedCommunication,
      content: selectedCommunication.content || selectedCommunication.description,
      metadata: selectedCommunication.metadata
        ? {
            ticketNumber: selectedCommunication.metadata.ticketNumber,
            invoiceNumber: selectedCommunication.metadata.invoiceNumber,
            status: selectedCommunication.metadata.status,
          }
        : undefined,
    };

    return (
      <>
        <Helmet>
          <title>{selectedCommunication.title} - Communications - Bloomcode CRM Portal</title>
        </Helmet>

        <div className="container mx-auto px-4 py-8">
          <CommunicationDetail
            communication={detailData}
            onBack={handleBackFromDetail}
            onNavigateToTicket={handleNavigateToTicket}
          />
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Communications - Bloomcode CRM Portal</title>
      </Helmet>

      <div className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <Breadcrumb className="mb-4">
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/">Home</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to="/dashboard">Dashboard</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>Communications</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        {/* Page Title */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-bold">Communications</h1>
            <p className="mt-1 text-muted-foreground">View your communication history with us.</p>
          </div>
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Controls */}
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          {/* Type Filter Tabs */}
          <Tabs value={typeFilter} onValueChange={(value) => setTypeFilter(value as TypeFilter)}>
            <TabsList className="flex-wrap h-auto gap-1">
              {filterTabs.map((tab) => (
                <TabsTrigger key={tab.value} value={tab.value} className="text-sm">
                  {tab.label}
                </TabsTrigger>
              ))}
            </TabsList>
          </Tabs>

          {/* View Toggle */}
          <div className="flex items-center gap-2">
            <ListIcon className="h-4 w-4 text-muted-foreground" />
            <Switch id="group-by-type" checked={groupByType} onCheckedChange={setGroupByType} />
            <Label htmlFor="group-by-type" className="flex items-center gap-2 cursor-pointer">
              <LayoutGrid className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm">Group by type</span>
            </Label>
          </div>
        </div>

        {/* Communication List */}
        <CommunicationList
          communications={communications}
          isLoading={isLoading}
          onCommunicationClick={handleCommunicationClick}
          groupByType={groupByType && typeFilter === 'all'}
        />
      </div>
    </>
  );
}
